import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AppImage from '../../components/AppImage';
import { regionalTeamService } from '../../services/regionalTeamService';
import { regionalTeamRosterService } from '../../services/regionalTeamRosterService';
import { useAuth } from '../../contexts/AuthContext';
import { isRegionAdmin, getRegionLabel } from '../../utils/tanzaniaRegions';

const RegionalTeamsProfiles = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const teamFromUrl = searchParams.get('team');
  const { userProfile } = useAuth();
  const regionAdmin = isRegionAdmin(userProfile?.role);
  const assignedRegion = userProfile?.region || '';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [players, setPlayers] = useState([]);
  const [officials, setOfficials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRoster, setIsLoadingRoster] = useState(false);
  const [error, setError] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Tournament Management' },
    { label: 'Regional Teams Profiles' },
  ];

  useEffect(() => {
    const loadTeams = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await regionalTeamService.getAll();
        const list = data || [];
        const scoped =
          regionAdmin && assignedRegion
            ? list.filter((team) => team.region === assignedRegion)
            : list;
        setTeams(scoped);
        if (teamFromUrl && scoped.some((team) => team.id === teamFromUrl)) {
          setSelectedTeamId(teamFromUrl);
        } else if (scoped.length) {
          setSelectedTeamId(scoped[0].id);
        } else {
          setSelectedTeamId(null);
        }
      } catch (err) {
        setError(err.message || 'Failed to load regional teams.');
      } finally {
        setIsLoading(false);
      }
    };
    loadTeams();
  }, [teamFromUrl, regionAdmin, assignedRegion]);

  useEffect(() => {
    const loadRoster = async () => {
      if (!selectedTeamId) {
        setPlayers([]);
        setOfficials([]);
        return;
      }
      setIsLoadingRoster(true);
      try {
        const [teamPlayers, teamOfficials] = await Promise.all([
          regionalTeamRosterService.getPlayers(selectedTeamId),
          regionalTeamRosterService.getOfficials(selectedTeamId),
        ]);
        setPlayers(teamPlayers || []);
        setOfficials(teamOfficials || []);
      } catch (err) {
        setError(err.message || 'Failed to load team roster.');
      } finally {
        setIsLoadingRoster(false);
      }
    };
    loadRoster();
  }, [selectedTeamId]);

  const selectedTeam = teams.find((team) => team.id === selectedTeamId);

  const regionNote = useMemo(() => {
    if (!regionAdmin || !assignedRegion) return null;
    return `View only for your region (${getRegionLabel(assignedRegion)})`;
  }, [regionAdmin, assignedRegion]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'}`}>
        <div className="p-6">
          <Breadcrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between mt-4 mb-6 gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Regional Teams Profiles</h1>
                <p className="text-muted-foreground">
                  {regionAdmin
                    ? 'View only for your region'
                    : 'Browse registered regional teams and their rosters'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {!regionAdmin && (
                <Link to="/regional-team-registration">
                  <Button variant="outline" iconName="Plus" iconPosition="left">Register Team</Button>
                </Link>
              )}
              <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate('/admin-dashboard')}>
                Back
              </Button>
            </div>
          </div>

          {regionNote && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-muted/40 border-border text-sm text-muted-foreground">
              {regionNote}
            </div>
          )}

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive">{error}</div>
          )}

          {isLoading ? (
            <div className="p-8 flex items-center justify-center gap-3 text-muted-foreground">
              <Icon name="Loader2" size={20} className="animate-spin" />
              Loading regional teams...
            </div>
          ) : teams.length === 0 ? (
            <div className="bg-card rounded-lg border border-border card-shadow p-8 text-center">
              <Icon name="Map" size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-medium mb-2">No regional teams registered yet</p>
              {!regionAdmin && (
                <Link to="/regional-team-registration">
                  <Button iconName="Plus" iconPosition="left">Register Regional Team</Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="space-y-3">
                {teams.map((team) => (
                  <button
                    key={team.id}
                    type="button"
                    onClick={() => setSelectedTeamId(team.id)}
                    className={`w-full text-left bg-card rounded-lg border p-4 transition-all ${
                      selectedTeamId === team.id
                        ? 'border-primary card-shadow'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                        {team.logoUrl ? (
                          <AppImage src={team.logoUrl} alt={team.teamName} className="w-full h-full object-contain" />
                        ) : (
                          <Icon name="Shield" size={20} className="text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{team.teamName}</p>
                        <p className="text-xs text-muted-foreground">
                          {getRegionLabel(team.region)}
                          {team.registrationStatus
                            ? ` · ${team.registrationStatus}`
                            : ''}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="xl:col-span-2 bg-card rounded-lg border border-border card-shadow p-6 space-y-6">
                {selectedTeam && (
                  <>
                    <div className="flex items-start gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                        {selectedTeam.logoUrl ? (
                          <AppImage
                            src={selectedTeam.logoUrl}
                            alt={selectedTeam.teamName}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Icon name="Shield" size={28} className="text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-foreground">{selectedTeam.teamName}</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                          Region: {getRegionLabel(selectedTeam.region)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Manager: {selectedTeam.teamManager || '—'} · Coach: {selectedTeam.headCoach || '—'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Doctor: {selectedTeam.teamDoctor || '—'} · Statistician: {selectedTeam.teamStatistician || '—'}
                        </p>
                      </div>
                    </div>

                    {isLoadingRoster ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Loader2" size={16} className="animate-spin" />
                        Loading roster...
                      </div>
                    ) : (
                      <>
                        <div>
                          <h3 className="font-semibold text-foreground mb-3">Players ({players.length})</h3>
                          {players.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No players registered for this team.</p>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {players.map((player) => (
                                <div key={player.id} className="border border-border rounded-lg p-3 flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                                    {player.photoUrl ? (
                                      <AppImage src={player.photoUrl} alt={player.fullName} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Icon name="User" size={16} className="text-muted-foreground" />
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground text-sm">{player.fullName}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {player.position || '—'} · Age {player.age ?? '—'}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <h3 className="font-semibold text-foreground mb-3">Officials ({officials.length})</h3>
                          {officials.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No other officials registered.</p>
                          ) : (
                            <div className="space-y-2">
                              {officials.map((official) => (
                                <div key={official.id} className="border border-border rounded-lg px-3 py-2 flex justify-between gap-3">
                                  <span className="font-medium text-foreground text-sm">{official.fullName}</span>
                                  <span className="text-xs text-muted-foreground">{official.roleTitle}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RegionalTeamsProfiles;
